/**
 * apiFetch — drop-in fetch wrapper that adds JWT auth headers
 * and handles 401 responses by clearing the token.
 *
 * Also extracts `x-request-id` from backend responses and attaches it
 * to the response object as `response.requestId` for observability.
 *
 * Usage:
 *   import { apiFetch } from '../services/apiFetch';
 *   const res = await apiFetch('/agents');
 *   console.log(res.requestId); // request id from backend
 */

import { getToken, clearToken } from './tokenStore';
import { createLogger } from '../utils/logger';

const log = createLogger('apiFetch');

// In production VITE_BACKEND_URL is the absolute backend URL; dev uses relative paths.
const BACKEND_PREFIX = (import.meta.env.VITE_BACKEND_URL && import.meta.env.VITE_BACKEND_URL !== '/')
  ? import.meta.env.VITE_BACKEND_URL.replace(/\/$/, '')
  : '';

/** Prepend the backend prefix to a relative path (no-op if url is already absolute). */
export function backendUrl(path) {
  if (!path || path.startsWith('http')) return path;
  return `${BACKEND_PREFIX}${path}`;
}

/**
 * Authenticated fetch wrapper.
 * Automatically injects `Authorization: Bearer <token>` when a token exists.
 * On 401, clears the stored token and redirects to login (page reload).
 */
export async function apiFetch(url, options = {}) {
  const token = getToken();
  const headers = { ...(options.headers || {}) };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(backendUrl(url), { ...options, headers });

  // Extract request_id from backend for correlation
  const requestId = response.headers?.get?.('x-request-id') || null;
  if (requestId) {
    response.requestId = requestId;
  }

  if (response.status === 401) {
    clearToken();
    log.warn('session_expired', { url, requestId });
    window.location.replace('/');
    throw new Error('Session expired. Please log in again.');
  }

  if (!response.ok) {
    log.warn('api_error', { url, status: response.status, requestId });
  }

  return response;
}
